/**
 * Signup Domain Errors
 * Typed errors for user signup with email verification and GDPR consent
 *
 * Error Hierarchy:
 * - SignupError (base)
 *   ├── SignupValidationError
 *   ├── UserAlreadyExistsError
 *   ├── WeakPasswordError
 *   └── VerificationCodeError
 *
 * Usage:
 * ```typescript
 * // Form validation
 * if (!email) {
 *   throw new SignupValidationError("Email is required", "email");
 * }
 *
 * // User already exists (Cognito)
 * catch (error) {
 *   if (error.code === "UsernameExistsException") {
 *     throw new UserAlreadyExistsError(email);
 *   }
 * }
 *
 * // Password too weak
 * if (!meetsRequirements) {
 *   throw new WeakPasswordError([
 *     "At least 8 characters",
 *     "At least one uppercase letter"
 *   ]);
 * }
 *
 * // Verification code issues
 * if (codeExpired) {
 *   throw new VerificationCodeError(
 *     "Verification code has expired",
 *     "expired"
 *   );
 * }
 * ```
 */

/**
 * Base class for all signup-related errors
 */
export class SignupError extends Error {
  constructor(message: string, public readonly originalError?: Error) {
    super(message);
    this.name = "SignupError";
  }
}

/**
 * Form validation error for signup fields
 *
 * @example
 * throw new SignupValidationError("Email format is invalid", "email");
 */
export class SignupValidationError extends SignupError {
  constructor(
    message: string,
    public readonly field: string
  ) {
    super(message);
    this.name = "SignupValidationError";
  }
}

/**
 * Error when user email already exists in Cognito
 *
 * Typically thrown when catching Cognito's UsernameExistsException
 *
 * @example
 * throw new UserAlreadyExistsError("user@example.com");
 */
export class UserAlreadyExistsError extends SignupError {
  constructor(public readonly email: string) {
    super(`User with email ${email} already exists`);
    this.name = "UserAlreadyExistsError";
  }
}

/**
 * Error when password doesn't meet Cognito requirements
 *
 * @example
 * throw new WeakPasswordError([
 *   "At least 8 characters",
 *   "At least one uppercase letter",
 *   "At least one number"
 * ]);
 */
export class WeakPasswordError extends SignupError {
  constructor(public readonly requirements: string[]) {
    const message = `Password does not meet requirements: ${requirements.join(", ")}`;
    super(message);
    this.name = "WeakPasswordError";
  }
}

/**
 * Error for invalid or expired verification code
 *
 * @example
 * // Invalid code
 * throw new VerificationCodeError("Invalid verification code", "invalid");
 *
 * // Expired code
 * throw new VerificationCodeError("Verification code has expired", "expired");
 */
export class VerificationCodeError extends SignupError {
  constructor(
    message: string,
    public readonly reason: "invalid" | "expired"
  ) {
    super(message);
    this.name = "VerificationCodeError";
  }
}
